import { useCallback, useEffect, useMemo, useState, memo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, Typography } from '@material-ui/core';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import TimeToHireSummaryCards from 'components/shared/timeToHireSummaryCards';
import PageContainer from '../../shared/pageContainer';
import NoDataText from '../../shared/noDataText';
import SelectField from '../../shared/selectField';
import { ReactComponent as CalendarIcon } from '../../../assets/icons/insert_invitation.svg';
import {
  API_TIME_TO_HIRE_BY_DEPARTMENT,
  API_TIME_TO_HIRE_BY_RECRUITER,
  API_TIME_TO_HIRE_BY_JOB,
  API_TIME_TO_HIRE_STATISTICS,
} from '../../../constants/apiRoutes';
import { APP_PAGES } from '../../../constants/pages';
import { TIME_TO_HIRE_DEFAULT_PERIOD } from '../../../constants/timeToHire';
import { isArrayEmpty } from '../../../utility/helpers';
import http from '../../../utility/http';
import { useTranslations } from '../../../utility/useTranslations';
import {
  buildDepartmentChartData,
  buildGroupedBarChartData,
  getDepartmentBarLabelsPlugin,
  getGroupedBarLabelsPlugin,
  filterDepartmentDataByPeriod,
  getTimeToHirePeriodOptions,
  getTimeToHirePeriodParams,
  prepareDepartmentChartOptions,
  prepareGroupedBarChartOptions,
  GROUPED_CHART_MAX_SELECTIONS,
  getGroupedBarChartSelectOptions,
  getInitialGroupedChartSelection,
  getOrderedGroupedChartData,
  formatRecruiterChartLabel,
  formatJobChartLabel,
  prepareGroupedChartControlsHeight,
} from './config';
import { sticky } from '../../../constants/helperCssRules';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  periodFilter: {
    width: 250,
  },
  pageDescription: {
    padding: spacing(8, 0, 6, 0),
    ...sticky(primary.white, 105),
  },
  chartSection: {
    paddingTop: spacing(10.5),
  },
  chartTitle: {
    marginBottom: spacing(6),
  },
  chartSelect: {
    width: '100%',
  },
  chartWrapper: {
    height: 320,
    position: 'relative',
  },
  emptyChartMessage: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 1,
    textAlign: 'center',
    width: '100%',
  },
  groupedChartsSection: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 48,
    paddingTop: spacing(12),
    alignItems: 'start',
  },
  groupedChartColumn: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  groupedChartControls: {
    width: '100%',
  },
  groupedChartWrapper: {
    height: 320,
    position: 'relative',
    flexShrink: 0,
    width: '100%',
  },
  chartNote: {
    marginTop: spacing(2),
  },
}));

const TimeToHireReportPage = ({ accessControl, ...rest }) => {
  const classes = useStyles();
  const translations = useTranslations(APP_PAGES.TIME_TO_HIRE_REPORT);
  const [period, setPeriod] = useState(TIME_TO_HIRE_DEFAULT_PERIOD);
  const [timeToHireByDepartment, setTimeToHireByDepartment] = useState([]);
  const [timeToHireByRecruiter, setTimeToHireByRecruiter] = useState([]);
  const [timeToHireByJob, setTimeToHireByJob] = useState([]);
  const [statisticsSummary, setStatisticsSummary] = useState({
    average: 0,
    median: 0,
    hires: 0,
  });
  const [selectedRecruiters, setSelectedRecruiters] = useState([]);
  const [selectedJobs, setSelectedJobs] = useState([]);

  const periodOptions = useMemo(
    () => getTimeToHirePeriodOptions(translations.periodOptions),
    [translations.periodOptions]
  );

  const periodParams = useMemo(
    () => getTimeToHirePeriodParams(period),
    [period]
  );

  const fetchDepartmentData = useCallback(async () => {
    try {
      const { data } = await http.get(API_TIME_TO_HIRE_BY_DEPARTMENT, {
        params: periodParams,
      });

      setTimeToHireByDepartment(Array.isArray(data) ? data : []);
    } catch {
      setTimeToHireByDepartment([]);
    }
  }, [periodParams]);

  const fetchRecruiterData = useCallback(async () => {
    try {
      const { data } = await http.get(API_TIME_TO_HIRE_BY_RECRUITER, {
        params: periodParams,
      });

      setTimeToHireByRecruiter(data);
      setSelectedRecruiters(getInitialGroupedChartSelection(data));
    } catch {
      setTimeToHireByRecruiter([]);
    }
  }, [periodParams]);

  const fetchJobData = useCallback(async () => {
    try {
      const { data } = await http.get(API_TIME_TO_HIRE_BY_JOB, {
        params: periodParams,
      });

      setTimeToHireByJob(data);
      setSelectedJobs(getInitialGroupedChartSelection(data));
    } catch {
      setTimeToHireByJob([]);
    }
  }, [periodParams]);

  const fetchStatistics = useCallback(async () => {
    try {
      const { data } = await http.get(API_TIME_TO_HIRE_STATISTICS, {
        params: periodParams,
      });

      setStatisticsSummary(data);
    } catch {
      setStatisticsSummary({});
    }
  }, [periodParams]);

  const initialData = useCallback(async () => {
    await Promise.all([
      fetchDepartmentData(),
      fetchRecruiterData(),
      fetchJobData(),
      fetchStatistics(),
    ]);
  }, [fetchDepartmentData, fetchRecruiterData, fetchJobData, fetchStatistics]);

  useEffect(() => {
    initialData();
  }, [initialData]);

  const departmentChartData = useMemo(
    () => filterDepartmentDataByPeriod(timeToHireByDepartment, period),
    [timeToHireByDepartment, period]
  );

  const hasDepartmentChartResults = !isArrayEmpty(departmentChartData);

  const chartData = useMemo(
    () => buildDepartmentChartData(timeToHireByDepartment, period),
    [timeToHireByDepartment, period]
  );

  const chartOptions = useMemo(
    () =>
      prepareDepartmentChartOptions(
        translations,
        period,
        timeToHireByDepartment
      ),
    [translations, period, timeToHireByDepartment]
  );

  const departmentBarLabelsPlugin = useMemo(
    () => getDepartmentBarLabelsPlugin(translations),
    [translations]
  );

  const recruiterSelectOptions = useMemo(
    () => getGroupedBarChartSelectOptions(timeToHireByRecruiter),
    [timeToHireByRecruiter]
  );

  const jobSelectOptions = useMemo(
    () => getGroupedBarChartSelectOptions(timeToHireByJob),
    [timeToHireByJob]
  );

  const filteredRecruiterData = useMemo(
    () => getOrderedGroupedChartData(timeToHireByRecruiter, selectedRecruiters),
    [timeToHireByRecruiter, selectedRecruiters]
  );

  const filteredJobData = useMemo(
    () => getOrderedGroupedChartData(timeToHireByJob, selectedJobs),
    [timeToHireByJob, selectedJobs]
  );

  const hasRecruiterChartResults = !isArrayEmpty(filteredRecruiterData);

  const hasJobChartResults = !isArrayEmpty(filteredJobData);

  const recruiterChartData = useMemo(
    () =>
      buildGroupedBarChartData(
        translations,
        filteredRecruiterData,
        formatRecruiterChartLabel
      ),
    [filteredRecruiterData, translations]
  );

  const jobChartData = useMemo(
    () =>
      buildGroupedBarChartData(
        translations,
        filteredJobData,
        formatJobChartLabel
      ),
    [filteredJobData, translations]
  );

  const recruiterChartOptions = useMemo(
    () => prepareGroupedBarChartOptions(filteredRecruiterData),
    [filteredRecruiterData]
  );

  const jobChartOptions = useMemo(
    () => prepareGroupedBarChartOptions(filteredJobData),
    [filteredJobData]
  );

  const groupedBarLabelsPlugin = useMemo(() => getGroupedBarLabelsPlugin(), []);

  const groupedChartControlsHeight = useMemo(
    () =>
      prepareGroupedChartControlsHeight(
        Math.max(selectedRecruiters.length, selectedJobs.length)
      ),
    [selectedRecruiters.length, selectedJobs.length]
  );

  const isRecruiterOptionDisabled = useCallback(
    option =>
      !selectedRecruiters.includes(option.value) &&
      selectedRecruiters.length >= GROUPED_CHART_MAX_SELECTIONS,
    [selectedRecruiters]
  );

  const isJobOptionDisabled = useCallback(
    option =>
      !selectedJobs.includes(option.value) &&
      selectedJobs.length >= GROUPED_CHART_MAX_SELECTIONS,
    [selectedJobs]
  );

  const handleRecruiterSelectChange = useCallback(selectedValues => {
    setSelectedRecruiters(
      (selectedValues || []).slice(0, GROUPED_CHART_MAX_SELECTIONS)
    );
  }, []);

  const handleJobSelectChange = useCallback(selectedValues => {
    setSelectedJobs(
      (selectedValues || []).slice(0, GROUPED_CHART_MAX_SELECTIONS)
    );
  }, []);

  const handleChangePeriod = useCallback(value => {
    setPeriod(currentPeriod =>
      value !== currentPeriod ? value : currentPeriod
    );
  }, []);

  return (
    <PageContainer
      {...rest}
      accessControl={accessControl}
      translations={translations}
      shouldPassProps={false}
      isFullWidthContent
    >
      <div className={classes.pageDescription}>
        <Typography variant="body2">{translations.description}</Typography>
      </div>
      <SelectField
        className={classes.periodFilter}
        label={translations.periodLabel}
        value={period}
        options={periodOptions}
        onChange={handleChangePeriod}
        startIcon={CalendarIcon}
        isSearchDisabled
      />
      <TimeToHireSummaryCards
        translations={translations}
        summary={statisticsSummary}
      />
      <div className={classes.chartSection}>
        <Typography className={classes.chartTitle} variant="h3">
          {translations.departmentChartTitle}
        </Typography>
        <div className={classes.chartWrapper}>
          <Bar
            data={chartData}
            options={chartOptions}
            plugins={[departmentBarLabelsPlugin]}
          />
          {!hasDepartmentChartResults && (
            <NoDataText
              className={classes.emptyChartMessage}
              text={translations.noResultsForPeriod}
            />
          )}
        </div>
      </div>
      <div className={classes.groupedChartsSection}>
        <div className={classes.groupedChartColumn}>
          <Typography className={classes.chartTitle} variant="h3">
            {translations.recruiterChartTitle}
          </Typography>
          <div
            className={classes.groupedChartControls}
            style={{ minHeight: groupedChartControlsHeight }}
          >
            <SelectField
              className={classes.chartSelect}
              label={translations.recruiterSelectLabel}
              placeholder={translations.recruiterSelectPlaceholder}
              value={selectedRecruiters}
              options={recruiterSelectOptions}
              onChange={handleRecruiterSelectChange}
              isMulti
              isFullWidth
              isOptionDisabled={isRecruiterOptionDisabled}
            />
          </div>
          <div className={classes.groupedChartWrapper}>
            <Bar
              data={recruiterChartData}
              options={recruiterChartOptions}
              plugins={[groupedBarLabelsPlugin]}
            />
            {!hasRecruiterChartResults && (
              <NoDataText
                className={classes.emptyChartMessage}
                text={translations.noResultsForPeriod}
              />
            )}
          </div>
          <Typography className={classes.chartNote} variant="caption">
            {translations.recruiterChartNote}
          </Typography>
        </div>
        <div className={classes.groupedChartColumn}>
          <Typography className={classes.chartTitle} variant="h3">
            {translations.jobChartTitle}
          </Typography>
          <div
            className={classes.groupedChartControls}
            style={{ minHeight: groupedChartControlsHeight }}
          >
            <SelectField
              className={classes.chartSelect}
              label={translations.jobSelectLabel}
              placeholder={translations.jobSelectPlaceholder}
              value={selectedJobs}
              options={jobSelectOptions}
              onChange={handleJobSelectChange}
              isMulti
              isFullWidth
              isOptionDisabled={isJobOptionDisabled}
            />
          </div>
          <div className={classes.groupedChartWrapper}>
            <Bar
              data={jobChartData}
              options={jobChartOptions}
              plugins={[groupedBarLabelsPlugin]}
            />
            {!hasJobChartResults && (
              <NoDataText
                className={classes.emptyChartMessage}
                text={translations.noResultsForPeriod}
              />
            )}
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

TimeToHireReportPage.propTypes = {
  accessControl: PropTypes.object.isRequired,
};

export default memo(TimeToHireReportPage);
